import React from 'react';

import { dealTokens } from './designTokens';
import { SystemFormSummaryLayout } from './SystemLayout';

const SkeletonLoader = ({
  type = 'card',
  count = 1,
  rows = 10,
  className = '',
  showKpis = true,
  showAction = false,
  columns = 7,
  calendarView = 'month',
}) => {
  const renderCardSkeleton = () => (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse ${className}`}>
      <div className="w-full h-48 bg-gray-200"></div>
      <div className="p-6">
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          <div className="h-6 bg-gray-200 rounded-full w-14"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded-lg mb-3 w-3/4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200 rounded w-4/6"></div>
        </div>
      </div>
    </div>
  );

  const renderCompactCardSkeleton = () => (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse relative ${className}`}>
      <div className="w-full h-48 bg-gray-200"></div>
      <div className="p-4 pb-16">
        <div className="h-6 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-3 w-3/4"></div>
        <div className="flex gap-2 mb-3">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="h-8 bg-gray-200 rounded w-full"></div>
      </div>
    </div>
  );

  const renderProfessionalSkeleton = () => (
    <div className={`group text-center relative p-6 pb-20 ${className}`}>
      <div className="relative mb-6">
        <div className="w-32 h-32 mx-auto rounded-full bg-gray-200 animate-pulse"></div>
      </div>
      <div className="h-6 bg-gray-200 rounded mb-2 w-3/4 mx-auto animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded mb-4 w-1/2 mx-auto animate-pulse"></div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="h-8 bg-gray-200 rounded w-full animate-pulse"></div>
      </div>
    </div>
  );

  const renderHeaderSkeleton = () => (
    <div className={`text-center mb-16 ${className}`}>
      <div className="inline-flex items-center justify-center p-2 mb-6 rounded-full bg-gray-200 animate-pulse">
        <div className="w-6 h-6 rounded bg-gray-300"></div>
      </div>
      <div className="h-12 bg-gray-200 rounded-lg mx-auto mb-6 w-96 animate-pulse"></div>
      <div className="h-6 bg-gray-200 rounded-lg mx-auto w-3/4 animate-pulse"></div>
    </div>
  );

  const renderProviderSkeleton = () => (
    <section className="py-16 pt-40 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-10 animate-pulse">
          <div className="w-full h-96 bg-gray-200"></div>
        </div>

        {/* Provider Information Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-10 animate-pulse">
          <div className="mb-6">
            {/* Title */}
            <div className="h-12 bg-gray-200 rounded-lg mb-4 w-3/4"></div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="h-8 bg-gray-200 rounded-full w-20"></div>
              <div className="h-8 bg-gray-200 rounded-full w-24"></div>
              <div className="h-8 bg-gray-200 rounded-full w-16"></div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2 w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2 w-12"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2 w-14"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="border-t border-gray-200 pt-6">
            <div className="h-6 bg-gray-200 rounded mb-3 w-32"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>

        {/* Widget Container Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderRankListSkeleton = () => (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 ${className}`}>
      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2 mb-4" />
      <ul className="space-y-1">
        {Array.from({ length: rows }, (_, i) => (
          <li key={i} className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
            <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
            <div className="h-3.5 bg-gray-200 rounded animate-pulse flex-1" />
            <div className="h-3.5 bg-gray-200 rounded animate-pulse w-10 flex-shrink-0" />
          </li>
        ))}
      </ul>
    </div>
  );

  const renderProviderCardGridSkeleton = () => (
    <div className={`flex flex-wrap ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="m-1 w-full md:w-[calc(33.333%-8px)] lg:w-[calc(25%-8px)] flex-shrink-0 rounded-lg border border-gray-200 shadow-sm flex flex-col bg-white overflow-hidden"
        >
          <div className="flex items-center justify-between">
            <div className="h-7 bg-gray-200 rounded-tl-lg animate-pulse w-24" />
            <div className="h-7 bg-gray-200 animate-pulse w-16" />
          </div>
          <div className="flex flex-col items-center px-3 py-3 gap-2 flex-1">
            <div className="w-24 h-24 rounded bg-gray-200 animate-pulse" />
            <div className="h-3.5 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-full" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-5/6" />
          </div>
          <div className="flex items-end justify-between border-t border-gray-100 p-2 gap-2">
            <div className="flex items-center gap-1">
              <div className="w-7 h-7 rounded bg-gray-200 animate-pulse" />
              <div className="w-7 h-7 rounded bg-gray-200 animate-pulse" />
            </div>
            <div className="flex-1 h-8 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );

  // Matches onboard's SelectableCubeGroup (onboard/components/StaffCube.js), which always
  // renders at most 3 cubes — fixed count instead of `rows`-driven like the other grids here.
  // Grid with `minmax(0, 1fr)` columns (not bare `1fr` or a flex-wrap row) is what StaffCube.js
  // itself uses to guarantee all 3 land on one row at equal width — a flex-wrap row of
  // fixed-145px items wraps the third one to its own row the moment the container is narrower
  // than 3 * 145px + gaps, which is exactly the bug this skeleton used to reproduce.
  const renderStaffCubesSkeleton = () => (
    <div className={className} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, maxWidth: 459 }}>
      {Array.from({ length: 3 }, (_, i) => (
        <div
          key={i}
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 animate-pulse"
          style={{ minHeight: 140 }}
        >
          <div className="w-14 h-14 rounded-full bg-gray-200" />
          <div className="h-3 w-16 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );

  const renderUserCardGridSkeleton = () => (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="w-full sm:w-[calc(50%-9px)] md:w-[calc(33.333%-8px)] lg:w-[calc(25%-9px)] flex-shrink-0 rounded-lg border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col"
        >
          <div className="flex justify-end">
            <div className="h-6 w-20 bg-gray-200 animate-pulse rounded-bl-lg" />
          </div>
          <div className="flex flex-col items-center px-4 py-5 gap-3">
            <div className="w-[120px] h-[120px] rounded bg-gray-200 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
          </div>
          <div className="flex items-center justify-center gap-2 p-3 border-t border-gray-100">
            <div className="h-9 w-24 rounded bg-gray-200 animate-pulse" />
            <div className="h-9 w-24 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderProfileFormSkeleton = () => (
    <div className={`flex flex-wrap gap-4 ${className}`}>
      <div className="flex-1 min-w-[280px] bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col items-center gap-2 mb-6">
          <div className="w-20 h-20 rounded-full bg-gray-200 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-32" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-20" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-2.5 bg-gray-200 rounded animate-pulse w-20" />
              <div className="h-9 bg-gray-200 rounded animate-pulse w-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 min-w-[280px] bg-white rounded-xl border border-gray-200 p-6">
        <div className="h-4 bg-gray-200 rounded animate-pulse w-40 mb-4" />
        <div className="h-3 bg-gray-200 rounded animate-pulse w-full mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse flex-shrink-0" />
              <div className="h-3 bg-gray-200 rounded animate-pulse flex-1" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAddressCardGridSkeleton = () => (
    <div className={`grid gap-5 ${className}`} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" />
              <div className="h-2.5 bg-gray-200 rounded animate-pulse w-16" />
            </div>
            <div className="flex gap-1">
              <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
              <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
          <div className="p-4 flex-1 space-y-2">
            <div className="h-3.5 bg-gray-200 rounded animate-pulse w-1/2" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-full" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3 mt-2" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderSettingsFormSkeleton = () => (
    <div className={`max-w-xl mx-auto p-6 ${className}`}>
      <div className="h-5 bg-gray-200 rounded w-48 animate-pulse mb-2" />
      <div className="h-3 bg-gray-200 rounded w-72 animate-pulse mb-6" />
      <div className="bg-white rounded-xl border border-gray-200 px-4">
        {Array.from({ length: count > 1 ? count : 5 }, (_, i) => (
          <div key={i} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
            <div className="space-y-1.5">
              <div className="h-3.5 bg-gray-200 rounded animate-pulse w-36" />
              <div className="h-2.5 bg-gray-200 rounded animate-pulse w-52" />
            </div>
            <div className="h-6 w-11 bg-gray-200 rounded-full animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );

  // Inline modal form skeleton: intentionally has no outer card, border or
  // shadow because SystemDialog already provides that chrome.
  const renderSimpleFormSkeleton = () => (
    <div className={`w-full ${className}`}>
      <div className="h-3.5 w-36 rounded bg-gray-200 animate-pulse mb-2" />
      <div className="h-2.5 w-64 max-w-full rounded bg-gray-100 animate-pulse mb-5" />
      <div className="flex gap-2 mb-5">
        <div className="h-8 w-28 rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-8 w-28 rounded-lg bg-gray-100 animate-pulse" />
      </div>
      <div className="space-y-4">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="space-y-1.5">
            <div className="h-2.5 w-24 rounded bg-gray-100 animate-pulse" />
            <div className="h-9 w-full rounded-lg bg-gray-200 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );

  // Studio list views draw the PAGE, not each card's internals (thumbnail, buttons,
  // badges vary too much for one component-accurate skeleton). Two shapes:
  //  - studioGrid: just the content grid of plain card-sized blocks. Used inline,
  //    below a still-visible toolbar, for views that refetch on search/filter.
  //  - studioPage: adds the filter/action toolbar strip on top of that grid, for
  //    the initial full-page load (early-returned in place of the real toolbar).
  const renderStudioGridSkeleton = () => (
    <div className={`grid gap-4 ${className}`} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rounded-xl border border-gray-100 bg-gray-100 animate-pulse" style={{ height: 150 }} />
      ))}
    </div>
  );

  const renderStudioPageSkeleton = () => (
    <div className={`p-6 ${className}`}>
      {/* Filter / action toolbar strip */}
      <div className="flex items-center gap-2 mb-6">
        <div className="h-9 w-[76px] rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-9 w-full max-w-[260px] rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-9 w-24 rounded-lg bg-gray-200 animate-pulse" />
        <div className="flex-1" />
        {showAction && <div className="h-9 w-36 rounded-lg bg-gray-200 animate-pulse" />}
      </div>
      {renderStudioGridSkeleton()}
    </div>
  );

  // Top-level Coaching list pages include a title and description above the
  // toolbar. This mirrors that loaded layout so the controls do not shift down
  // when Athletes, Coaches or Plans finish loading.
  const renderCoachingListPageSkeleton = () => (
    <div className={`p-6 bg-white min-h-full ${className}`}>
      <div className="flex items-start gap-4 mb-[18px]">
        <div className="flex-1">
          <div className="h-6 w-36 rounded bg-gray-200 animate-pulse mb-2" />
          <div className="h-3.5 w-full max-w-lg rounded bg-gray-100 animate-pulse" />
        </div>
        <div className="h-9 w-36 rounded-lg bg-gray-200 animate-pulse" />
      </div>
      <div className="flex items-center gap-2 mb-4 rounded-xl border border-gray-100 p-3">
        <div className="h-9 w-[102px] rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-9 w-full max-w-[200px] rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-9 w-24 rounded-lg bg-gray-200 animate-pulse" />
      </div>
      {renderStudioGridSkeleton()}
    </div>
  );

  // Insights page: title, a row of KPI tiles, then two large section blocks
  // (chart + list) — the page's shape, not the individual widgets.
  const renderInsightsPageSkeleton = () => (
    <div className={`p-6 ${className}`}>
      <div className="h-6 w-40 rounded bg-gray-200 animate-pulse mb-2" />
      <div className="h-3.5 w-full max-w-md rounded bg-gray-100 animate-pulse mb-6" />
      <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-24 rounded-xl border border-gray-100 bg-gray-100 animate-pulse" />
        ))}
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {Array.from({ length: 2 }, (_, i) => (
          <div key={i} className="h-56 rounded-xl border border-gray-100 bg-gray-100 animate-pulse" />
        ))}
      </div>
    </div>
  );

  const renderReportPageSkeleton = () => (
    <div className={`p-5 bg-white min-h-full ${className}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-1">
          <div className="h-6 w-36 rounded bg-gray-200 animate-pulse mb-2" />
          <div className="h-3.5 w-full max-w-lg rounded bg-gray-100 animate-pulse" />
        </div>
        <div className="h-9 w-28 rounded-lg bg-gray-200 animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-24 rounded-xl border border-gray-100 bg-gray-100 animate-pulse" />
        ))}
      </div>
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 p-3 border-b border-gray-100">
          <div className="h-9 w-full max-w-[240px] rounded-lg bg-gray-200 animate-pulse" />
          <div className="h-9 w-24 rounded-lg bg-gray-200 animate-pulse" />
        </div>
        <div className="p-4">
          <div className="h-8 rounded-lg bg-gray-100 animate-pulse mb-2" />
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="h-10 border-b border-gray-100 flex items-center gap-4">
              <div className="h-3 flex-1 rounded bg-gray-100 animate-pulse" />
              <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
              <div className="h-3 w-16 rounded bg-gray-100 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Coaching athlete profile: identity/KPI sidebar on the left and the URL-tabbed
  // workspace on the right. Keeps the loaded shell's proportions stable.
  const renderCoachingProfileSkeleton = () => (
    <div className={`p-6 ${className}`}>
      <div className="flex items-center gap-5 rounded-xl border border-gray-100 bg-white p-5 mb-4">
        <div className="h-16 w-16 shrink-0 rounded-full bg-gray-200 animate-pulse" />
        <div className="flex-1">
          <div className="h-6 w-44 rounded bg-gray-200 animate-pulse mb-3" />
          <div className="flex gap-2">
            <div className="h-6 w-20 rounded-full bg-gray-100 animate-pulse" />
            <div className="h-6 w-28 rounded-full bg-gray-100 animate-pulse" />
          </div>
        </div>
        <div className="flex items-center gap-2 border-l border-gray-100 pl-4">
          <div className="h-9 w-9 rounded-full bg-gray-200 animate-pulse" />
          <div>
            <div className="h-3 w-14 rounded bg-gray-100 animate-pulse mb-2" />
            <div className="h-4 w-28 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
        <div className="h-9 w-9 rounded-lg bg-gray-100 animate-pulse" />
      </div>

      <div className="flex gap-3 border-b border-gray-200 pb-3 mb-5">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="h-7 rounded bg-gray-200 animate-pulse" style={{ width: i === 0 ? 92 : 76 }} />
        ))}
      </div>

      <div className="flex gap-6 items-start">
        <div className="w-[280px] shrink-0 rounded-xl border border-gray-100 bg-white p-5">
          <div className="space-y-3 mb-6">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-4 w-full rounded bg-gray-100 animate-pulse" />
            ))}
          </div>
          <div className="border-t border-gray-100 pt-5 space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-4 w-full rounded bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {showKpis && (
            <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))' }}>
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="h-24 rounded-xl border border-gray-100 bg-gray-100 animate-pulse" />
              ))}
            </div>
          )}
          <div className="h-64 rounded-xl border border-gray-100 bg-gray-100 animate-pulse" />
        </div>
      </div>
    </div>
  );

  // Bundle builder (newDesigns/bundleBuilder/BundleBuilder.js): the details card and the seven
  // slot cards on the left, the "Requisiti" panel and the offer card on the right.
  const renderBundleBuilderSkeleton = () => (
    <div className={`flex flex-wrap gap-6 p-6 ${className}`}>
      <div className="flex-1 min-w-[320px] space-y-3">
        <div className="rounded-xl border border-gray-100 bg-white p-5 space-y-3">
          <div className="h-9 w-full rounded-lg bg-gray-100 animate-pulse" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-9 rounded-lg bg-gray-100 animate-pulse" />
            ))}
          </div>
          <div className="h-16 w-full rounded-lg bg-gray-100 animate-pulse" />
        </div>
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4">
            <div className="h-11 w-11 rounded-lg bg-gray-200 animate-pulse shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 rounded bg-gray-200 animate-pulse w-1/3" />
              <div className="h-3 rounded bg-gray-100 animate-pulse w-1/2" />
            </div>
            <div className="h-7 w-16 rounded-lg bg-gray-100 animate-pulse" />
          </div>
        ))}
      </div>
      <div className="w-[320px] max-w-full space-y-4">
        <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-2">
          <div className="h-3.5 w-24 rounded bg-gray-200 animate-pulse mb-3" />
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-gray-200 animate-pulse shrink-0" />
              <div className="h-3 rounded bg-gray-100 animate-pulse" style={{ width: `${55 + (i % 3) * 15}%` }} />
            </div>
          ))}
        </div>
        <div className="h-48 rounded-xl border border-gray-100 bg-white animate-pulse" />
      </div>
    </div>
  );

  // Circuit/exercise builder modal (CircuitBuilderForm.js): library panel on the
  // left (search + filter chips + a scrollable list of rows) and the form on the
  // right (Dettagli + Classificazione cards, then the slots list placeholder) —
  // mirrors the loaded split-panel shape so nothing shifts once data arrives.
  const renderBuilderFormSkeleton = () => (
    <div className={`flex h-full ${className}`}>
      <div className="w-[300px] shrink-0 border-r border-gray-100 p-4">
        <div className="h-3 w-28 rounded bg-gray-200 animate-pulse mb-3" />
        <div className="h-9 w-full rounded-lg bg-gray-100 animate-pulse mb-3" />
        <div className="flex flex-wrap gap-2 mb-4">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-7 rounded-full bg-gray-100 animate-pulse" style={{ width: 60 + (i % 3) * 12 }} />
          ))}
        </div>
        <div className="space-y-2">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-gray-100 p-2">
              <div className="h-8 w-8 rounded bg-gray-200 animate-pulse shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 rounded bg-gray-200 animate-pulse w-3/4" />
                <div className="h-2.5 rounded bg-gray-100 animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 p-5 space-y-5">
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-20 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-32 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          </div>
          <div className="h-6 w-11 rounded-full bg-gray-100 animate-pulse" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-14 rounded-xl border border-gray-100 bg-gray-50 animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );

  const renderRecipeFormSkeleton = () => (
    <div className={className} aria-hidden="true" style={{ padding: dealTokens.space[6] }}>
      <SystemFormSummaryLayout
        summary={
          <div className="rounded-xl border border-gray-100 p-4 space-y-4">
            <div className="h-4 w-40 rounded bg-gray-200 animate-pulse" />
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-12 rounded bg-gray-100 animate-pulse" />
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} className="h-5 rounded bg-gray-100 animate-pulse" />
            ))}
          </div>
        }
      >
        <div className="space-y-5">
          <div className="h-4 w-40 rounded bg-gray-200 animate-pulse" />
          <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-20 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="h-12 rounded-lg bg-gray-100 animate-pulse" />
          ))}
        </div>
      </SystemFormSummaryLayout>
    </div>
  );

  // Exercise/meal builder form (ExerciseBuilderForm.js and siblings):
  // content column (Details/Equipment/Media cards) + a narrower classification
  // column (Classification/Muscle areas), matching the loaded 1fr/480px grid.
  const renderExerciseFormSkeleton = () => (
    <div className={`grid gap-5 ${className}`} style={{ gridTemplateColumns: 'minmax(0, 1fr) 480px' }}>
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-16 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="space-y-3">
            <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-16 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-28 rounded-lg bg-gray-100 animate-pulse" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-24 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="h-9 rounded-lg bg-gray-100 animate-pulse mb-3" />
          <div className="flex gap-2">
            <div className="h-7 w-20 rounded-full bg-gray-100 animate-pulse" />
            <div className="h-7 w-24 rounded-full bg-gray-100 animate-pulse" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-14 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="flex gap-3">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="h-[120px] flex-1 rounded-lg bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-28 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="flex flex-wrap gap-2 mb-4">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-7 rounded-lg bg-gray-100 animate-pulse" style={{ width: 70 + (i % 3) * 10 }} />
            ))}
          </div>
          <div className="h-6 w-11 rounded-full bg-gray-100 animate-pulse" />
        </div>
        <div className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-24 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="h-[170px] rounded-lg bg-gray-100 animate-pulse mb-3" />
          <div className="space-y-2">
            {Array.from({ length: rows }, (_, i) => (
              <div key={i} className="h-9 rounded-lg bg-gray-50 border border-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Mimics ExerciseBuilderForm.js's "how would you like to create this?"
  // chooser (3 FlatCards: from scratch / copy from existing / generate with
  // AI) — shown behind ExerciseFormModal.js's lazy-load Suspense fallback,
  // which used to be a bare "Loading editor…" text.
  const renderExerciseCreateChooserSkeleton = () => (
    <div className={`flex gap-3.5 ${className}`} style={{ maxWidth: 780 }}>
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex-1 rounded-xl border border-gray-100 p-5">
          <div className="h-6 w-6 rounded bg-gray-200 animate-pulse mb-3" />
          <div className="h-3.5 w-2/3 rounded bg-gray-200 animate-pulse mb-2.5" />
          <div className="space-y-1.5">
            <div className="h-2.5 rounded bg-gray-100 animate-pulse w-full" />
            <div className="h-2.5 rounded bg-gray-100 animate-pulse w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );

  // Two-equal-column form (SessionForm.js and siblings — OfferForm.js/ClassForm.js):
  // left card has several stacked fields, right card (Location) has just a
  // couple — matching the loaded `minmax(0,1fr) minmax(0,1fr)` grid.
  const renderSessionFormSkeleton = () => (
    <div className={`grid gap-5 ${className}`} style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
      <div className="rounded-xl border border-gray-100 p-4">
        <div className="h-3.5 w-28 rounded bg-gray-200 animate-pulse mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          ))}
          <div className="flex gap-3">
            <div className="h-9 flex-1 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-9 flex-1 rounded-lg bg-gray-100 animate-pulse" />
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-gray-100 p-4">
        <div className="h-3.5 w-20 rounded bg-gray-200 animate-pulse mb-4" />
        <div className="space-y-3">
          <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-24 rounded-lg bg-gray-100 animate-pulse" />
        </div>
      </div>
    </div>
  );

  // Assign-to-user forms (AddVideoToUser.js and siblings): 3 stacked
  // SystemFormSection-style cards — recipient/item picker, item details,
  // payment — with a varying field count per section, matching the loaded
  // SystemFormGrid 2-column shape so the modal doesn't jump in height once
  // data arrives.
  const renderAssignmentFormSkeleton = () => (
    <div className={`flex flex-col gap-4 ${className}`}>
      {[2, 6, 4].map((fields, sectionIndex) => (
        <div key={sectionIndex} className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-36 rounded bg-gray-200 animate-pulse mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {Array.from({ length: fields }, (_, fieldIndex) => (
              <div key={fieldIndex} className="space-y-1.5">
                <div className="h-2.5 w-24 rounded bg-gray-100 animate-pulse" />
                <div className="h-9 w-full rounded-lg bg-gray-200 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Add wallet dialog: mirrors the three loaded SystemFormSection cards and
  // their actual 3 / 2 / 2 field distribution so the modal keeps its height
  // and rhythm while the customer list is loading.
  const renderWalletFormSkeleton = () => (
    <div className={`flex flex-col gap-4 ${className}`}>
      {[3, 2, 2].map((fields, sectionIndex) => (
        <div key={sectionIndex} className="rounded-xl border border-gray-100 p-4">
          <div className="h-3.5 w-40 rounded bg-gray-200 animate-pulse mb-2" />
          <div className="h-2.5 w-64 max-w-full rounded bg-gray-100 animate-pulse mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {Array.from({ length: fields }, (_, fieldIndex) => (
              <div key={fieldIndex} className="space-y-1.5">
                <div className="h-2.5 w-24 rounded bg-gray-100 animate-pulse" />
                <div className="h-9 w-full rounded-lg bg-gray-200 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // A single identity row — avatar + two-line name/email + a status pill — followed by a
  // caption line. Matches CognitoUserInformation.js's real shape (bordered card, not a form
  // with labeled inputs), which simpleForm/profileForm both got visibly wrong for it.
  const renderAccountStatusSkeleton = () => (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-gray-200 animate-pulse shrink-0" />
          <div className="space-y-1.5 min-w-0">
            <div className="h-3 w-32 rounded bg-gray-200 animate-pulse" />
            <div className="h-2.5 w-40 rounded bg-gray-100 animate-pulse" />
          </div>
        </div>
        <div className="h-5 w-20 rounded-full bg-gray-200 animate-pulse shrink-0" />
      </div>
      <div className="h-2.5 w-56 max-w-full rounded bg-gray-100 animate-pulse" />
    </div>
  );

  const renderChatSkeleton = () => (
    <div className={`flex flex-col gap-3 p-4 w-full ${className}`}>
      {[1, 0, 1, 1, 0, 1].map((isOwn, i) => (
        <div key={i} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <div className="h-9 rounded-2xl bg-gray-200 animate-pulse" style={{ width: [180, 130, 210, 150, 190, 120][i % 6] }} />
        </div>
      ))}
    </div>
  );

  // Calendar (providers/calendar), shaped like the Google-style grids. `columns` follows the
  // 5/6/7-day week setting. calendarMonth / calendarTimeGrid are the GRID only — shown while
  // a period refetches, under the toolbar that stays — and calendarPage adds the toolbar for
  // the first load, when there is nothing on screen yet, with the grid of `calendarView`.
  const renderCalendarMonthSkeleton = () => (
    <div className={`h-full flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden ${className}`}>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array.from({ length: columns }, (_, i) => (
          <div key={i} className="flex justify-center py-3">
            <div className="h-2.5 w-8 rounded bg-gray-200 animate-pulse" />
          </div>
        ))}
      </div>
      <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, gridAutoRows: '1fr' }}>
        {Array.from({ length: columns * 5 }, (_, i) => (
          <div key={i} className={`border-t border-gray-100 p-2 flex flex-col items-center gap-2 ${i % columns ? 'border-l' : ''}`}>
            <div className="h-6 w-6 rounded-full bg-gray-200 animate-pulse" />
            {i % 3 === 0 && <div className="h-4 w-full rounded-md bg-gray-100 animate-pulse" />}
            {i % 5 === 1 && <div className="h-4 w-4/5 rounded-md bg-gray-100 animate-pulse" />}
          </div>
        ))}
      </div>
    </div>
  );

  const renderCalendarTimeGridSkeleton = () => (
    <div className={`h-full flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden ${className}`}>
      <div className="flex border-b border-gray-100">
        <div className="w-14 flex-shrink-0" />
        <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }, (_, i) => (
            <div key={i} className="flex flex-col items-center gap-2 py-3">
              <div className="h-2.5 w-8 rounded bg-gray-200 animate-pulse" />
              <div className="h-9 w-9 rounded-full bg-gray-200 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <div className="w-14 flex-shrink-0 flex flex-col">
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="h-14 flex justify-end pr-2 -mt-1.5">
              {i > 0 && <div className="h-2.5 w-8 rounded bg-gray-100 animate-pulse" />}
            </div>
          ))}
        </div>
        <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }, (_, col) => (
            <div key={col} className="relative border-l border-gray-100">
              {Array.from({ length: 10 }, (_, row) => (
                <div key={row} className="h-14 border-t border-gray-100" />
              ))}
              <div
                className="absolute left-1 right-1 rounded-md bg-gray-100 animate-pulse"
                style={{ top: `${56 * ((col * 2 + 1) % 7) + 6}px`, height: `${col % 2 ? 72 : 44}px` }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // One labelled form field (label line + 36px box), for a field whose options are still
  // loading inside an otherwise ready form — e.g. a filter that depends on another one.
  const renderFormFieldSkeleton = () => (
    <div className={`w-full ${className}`}>
      <div className="h-2.5 w-24 rounded bg-gray-200 animate-pulse mb-2" />
      <div className="h-9 w-full rounded-lg bg-gray-100 animate-pulse" />
    </div>
  );

  // "Storico prenotazioni" (calendar/newDesigns/UserReservationsHistory.js): the summary
  // (past, future, presence badges), then the two lists of reservation rows with a badge.
  const renderReservationsHistorySkeleton = () => (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
        <div className="h-4 w-40 rounded bg-gray-200 animate-pulse" />
        {[0, 1].map(i => (
          <div key={i} className="flex justify-between gap-3">
            <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
            <div className="h-3 w-8 rounded bg-gray-200 animate-pulse" />
          </div>
        ))}
        <div className="flex justify-between gap-3">
          <div className="h-3 w-20 rounded bg-gray-100 animate-pulse" />
          <div className="flex gap-1">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-5 w-20 rounded-full bg-gray-100 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
      {[3, 2].map((rowsCount, list) => (
        <div key={list} className="rounded-xl border border-gray-200 p-4 flex flex-col">
          <div className="h-4 w-28 mb-3 rounded bg-gray-200 animate-pulse" />
          {Array.from({ length: rowsCount }, (_, i) => (
            <div key={i} className={`flex items-center justify-between gap-3 py-2 ${i ? 'border-t border-gray-100' : ''}`}>
              <div className="h-3 w-48 rounded bg-gray-100 animate-pulse" />
              <div className="h-5 w-20 rounded-full bg-gray-100 animate-pulse" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // The calendar event card (CalendarEventCard.js) while its data loads: action icons,
  // colour square + title + date + badge, a customer/participants row, then detail lines.
  const renderEventCardSkeleton = () => (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="h-8 w-28 rounded-lg bg-gray-100 animate-pulse" />
        <div className="flex gap-1">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-8 w-8 rounded-full bg-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <div className="h-3.5 w-3.5 mt-1.5 rounded bg-gray-200 animate-pulse" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="h-5 w-3/5 rounded bg-gray-200 animate-pulse" />
          <div className="h-3 w-2/5 rounded bg-gray-100 animate-pulse" />
          <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
          <div className="h-5 w-16 rounded-full bg-gray-100 animate-pulse" />
        </div>
      </div>
      <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
        <div className="h-10 w-10 rounded-full bg-gray-100 animate-pulse" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse" />
          <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
        </div>
      </div>
      {[0, 1, 2].map(i => (
        <div key={i} className="flex justify-between gap-3">
          <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
          <div className="h-3 w-20 rounded bg-gray-200 animate-pulse" />
        </div>
      ))}
    </div>
  );

  // Calendar event details modal: date/status header, participants left, details right.
  const renderEventDetailsSkeleton = () => (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className="flex items-center gap-3">
        <div className="h-6 w-56 rounded bg-gray-200 animate-pulse" />
        <div className="h-6 w-20 rounded-full bg-gray-100 animate-pulse" />
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(280px, 1fr)' }}>
        <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
          <div className="h-4 w-28 rounded bg-gray-200 animate-pulse" />
          {[0, 1].map(row => (
            <div key={row} className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-100 animate-pulse" />
              <div className="flex-1 flex flex-col gap-2">
                <div className="h-3 w-40 rounded bg-gray-200 animate-pulse" />
                <div className="h-3 w-56 rounded bg-gray-100 animate-pulse" />
              </div>
              <div className="h-8 w-40 rounded-lg bg-gray-100 animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
          <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" />
          {[0, 1, 2, 3, 4].map(row => (
            <div key={row} className="flex justify-between gap-3">
              <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
              <div className="h-3 w-20 rounded bg-gray-200 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Time-slot buttons of SystemTimeSlotPicker (3 per row).
  const renderTimeSlotsSkeleton = () => (
    <div className={`grid grid-cols-3 gap-2 ${className}`}>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="h-9 rounded-lg bg-gray-100 animate-pulse" />
      ))}
    </div>
  );

  const renderCalendarPageSkeleton = () => (
    <div className={`h-full flex flex-col gap-4 ${className}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-16 rounded-lg bg-gray-200 animate-pulse" />
          <div className="h-9 w-9 rounded-full bg-gray-100 animate-pulse" />
          <div className="h-9 w-9 rounded-full bg-gray-100 animate-pulse" />
          <div className="h-6 w-48 rounded bg-gray-200 animate-pulse ml-2" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-9 w-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-9 w-24 rounded-lg bg-gray-100 animate-pulse" />
          <div className="h-9 w-28 rounded-lg bg-gray-200 animate-pulse" />
        </div>
      </div>
      <div className="flex-1 min-h-0">
        {calendarView === 'agenda'
          ? renderRankListSkeleton()
          : calendarView === 'month'
          ? renderCalendarMonthSkeleton()
          : renderCalendarTimeGridSkeleton()}
      </div>
    </div>
  );

  const renderSkeleton = () => {
    switch (type) {
      case 'calendarPage':
        return renderCalendarPageSkeleton();
      case 'formField':
        return renderFormFieldSkeleton();
      case 'timeSlots':
        return renderTimeSlotsSkeleton();
      case 'eventDetails':
        return renderEventDetailsSkeleton();
      case 'eventCard':
        return renderEventCardSkeleton();
      case 'reservationsHistory':
        return renderReservationsHistorySkeleton();
      case 'calendarMonth':
        return renderCalendarMonthSkeleton();
      case 'calendarTimeGrid':
        return renderCalendarTimeGridSkeleton();
      case 'chat':
        return renderChatSkeleton();
      case 'studioPage':
        return renderStudioPageSkeleton();
      case 'studioGrid':
        return renderStudioGridSkeleton();
      case 'coachingListPage':
        return renderCoachingListPageSkeleton();
      case 'insightsPage':
        return renderInsightsPageSkeleton();
      case 'reportPage':
        return renderReportPageSkeleton();
      case 'coachingProfile':
        return renderCoachingProfileSkeleton();
      case 'kpiCard':
        return (
          <div className={`rounded-xl border border-gray-100 bg-white p-4 animate-pulse ${className}`} aria-hidden="true">
            <div className="flex items-center justify-between mb-3">
              <div className="h-3 w-24 rounded bg-gray-200" />
              <div className="h-7 w-7 rounded-lg bg-gray-100" />
            </div>
            <div className="h-6 w-16 rounded bg-gray-200" />
          </div>
        );
      case 'card':
        return renderCardSkeleton();
      case 'compactCard':
        return renderCompactCardSkeleton();
      case 'professional':
        return renderProfessionalSkeleton();
      case 'header':
        return renderHeaderSkeleton();
      case 'provider':
        return renderProviderSkeleton();
      case 'settingsForm':
        return renderSettingsFormSkeleton();
      case 'simpleForm':
        return renderSimpleFormSkeleton();
      case 'rankList':
        return renderRankListSkeleton();
      case 'providerCardGrid':
        return renderProviderCardGridSkeleton();
      case 'userCardGrid':
        return renderUserCardGridSkeleton();
      case 'staffCubes':
        return renderStaffCubesSkeleton();
      case 'profileForm':
        return renderProfileFormSkeleton();
      case 'addressCardGrid':
        return renderAddressCardGridSkeleton();
      case 'builderForm':
        return renderBuilderFormSkeleton();
      case 'bundleBuilder':
        return renderBundleBuilderSkeleton();
      case 'exerciseForm':
        return renderExerciseFormSkeleton();
      case 'recipeForm':
      case 'mealForm':
        return renderRecipeFormSkeleton();
      case 'exerciseCreateChooser':
        return renderExerciseCreateChooserSkeleton();
      case 'sessionForm':
        return renderSessionFormSkeleton();
      case 'assignmentForm':
        return renderAssignmentFormSkeleton();
      case 'walletForm':
        return renderWalletFormSkeleton();
      case 'accountStatus':
        return renderAccountStatusSkeleton();
      default:
        return renderCardSkeleton();
    }
  };

  if (count === 1) {
    return renderSkeleton();
  }

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={index}>{renderSkeleton()}</div>
      ))}
    </>
  );
};

export default SkeletonLoader;
